import { Box, Grid, Typography, TextField, FormControl, FormControlLabel, InputLabel, Select, MenuItem, Collapse, Stack, Tooltip, IconButton, Popover, Switch, FormLabel } from "@mui/material";
import { type MouseEvent, type ReactNode, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { PaginationLoadingOverlay } from "../../../../clients/PaginationLoadingOverlay";
import { colors, alphaVar } from "../../../../ui/colors";
import { money } from "./helpers";
import { menuPropsModal } from "../../../../../utils";
import { EMonetaryAmountBy } from "../../../../invoice/create/interfaces/invoice.interfaces";
import { quoteCreateActions } from "../../../../../redux/features/quotes/quote-create-advanced";
import { KeyboardArrowUp, KeyboardArrowDown, ChatBubble, ChatBubbleOutline, AddCircleOutline, Edit } from "@mui/icons-material";
import { ServiceAliasField } from "@components/common/ServiceAliasField";
import { EFrequencyType, JOB_FREQUENCY_OPTIONS, getJobFrequencyLabel } from "@components/scope-of-work/helpers";

const monetaryAmountOptions = [
  {
    value: EMonetaryAmountBy.MONTHLY,
    label: "Monthly",
  },
  {
    value: EMonetaryAmountBy.YEARLY,
    label: "Yearly",
  },
  {
    value: EMonetaryAmountBy.UNIQUE,
    label: "One Time",
  },
  {
    value: EMonetaryAmountBy.PER_ITEM,
    label: "Per Item",
  },
  {
    value: EMonetaryAmountBy.PER_SQUARE_FOOT,
    label: "Per Square Foot",
  },
  {
    value: EMonetaryAmountBy.PER_CAR_SPACE,
    label: "Per Car Space",
  },
  {
    value: EMonetaryAmountBy.PER_FLOOR,
    label: "Per Floor",
  },
];

const normalizePriceDraft = (raw: string): string => {
  if (raw === "") return "";
  const cleaned = raw.replace(/[^\d.]/g, "");
  const dot = cleaned.indexOf(".");
  if (dot === -1) return cleaned;
  return `${cleaned.slice(0, dot + 1)}${cleaned.slice(dot + 1).replace(/\./g, "")}`;
};

interface TaskNoteBubbleForQuoteProps {
  areaUid: string;
  taskId: string | number;
  taskName: string;
  noteFromStore?: string;
  icon?: ReactNode;
}

const TaskNoteBubbleForQuote = ({ areaUid, taskId, taskName, noteFromStore = "", icon }: TaskNoteBubbleForQuoteProps) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const MAX_LENGTH = 100;
  const [note, setNote] = useState(noteFromStore);

  useEffect(() => {
    setNote(noteFromStore);
  }, [noteFromStore]);

  const open = Boolean(anchorEl);
  const id = open ? `note-popover-${taskId}` : undefined;
  const hasNote = (noteFromStore || "").trim().length > 0;

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    dispatch(quoteCreateActions.updateTaskNote({ areaUid, taskId, note }));
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title={hasNote ? "Edit note" : "Add note"}>
        <IconButton
          size="small"
          onClick={handleOpen}
          sx={{
            color: hasNote ? colors.orange.base : colors.gray[300],
            "&:hover": { color: colors.orange[500] },
            mr: 0.5,
            zIndex: 9999,
            position: "relative",
          }}
        >
          {icon ?? (hasNote ? <ChatBubble fontSize="small" /> : <ChatBubbleOutline fontSize="small" />)}
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 1.5,
            width: 260,
            borderRadius: 2,
            zIndex: 99999,
          },
        }}
        sx={{ zIndex: 99999 }}
      >
        <Box onClick={(e) => e.stopPropagation()}>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder={`Write a note for task: ${taskName || "Service"}...`}
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, MAX_LENGTH))}
            size="small"
            inputProps={{ maxLength: MAX_LENGTH }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                event.stopPropagation();

                handleClose();
              }

              if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();

                setAnchorEl(null);
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: colors.gray[200] },
                "&:hover fieldset": { borderColor: colors.orange[400] },
                "&.Mui-focused fieldset": { borderColor: colors.orange.base },
              },
              "& .MuiInputBase-input": { fontSize: 13 },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "right",
              mt: 0.5,
              color: note.length >= MAX_LENGTH ? colors.orange.base : colors.gray[400],
            }}
          >
            {note.length}/{MAX_LENGTH}
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export const QuoteCreateSummary = ({ summary, loading }: { summary: any; loading?: boolean }) => {
  const dispatch = useDispatch();
  const { sectorWorkflow } = summary;

  const [expandedAreaUid, setExpandedAreaUid] = useState<string | null>(null);
  const [hoverTaskUid, setHoverTaskUid] = useState<string | null>(null);
  const [openNotePreviewUid, setOpenNotePreviewUid] = useState<string | null>(null);
  const [isEditingFrequency, setIsEditingFrequency] = useState(false);

  const currentFrequencyType = sectorWorkflow?.frequencyType || summary?.quote?.scopeOfWork?.frequencyType || EFrequencyType.WEEKLY;
  const getAreaQuantity = (area: any): number => {
    if (area?.quantity != null) {
      const quantity = Number(area.quantity);

      return Number.isFinite(quantity) && quantity >= 0 ? quantity : 0;
    }

    const squareFootages = Array.isArray(area?.squareFootages) ? area.squareFootages : [];

    return squareFootages.reduce((total: number, item: any) => total + (Number(item?.quantity) || 0), 0);
  };

  const areas = Array.isArray(sectorWorkflow?.areas) ? sectorWorkflow.areas : [];

  const activeAreas = areas.filter((area: any) => getAreaQuantity(area) > 0);

  const otherAreas = Array.isArray(sectorWorkflow?.otherAreas) ? sectorWorkflow.otherAreas : [];

  const activeOtherAreas = otherAreas.filter((area: any) => getAreaQuantity(area) > 0);

  const selectedObjects =
    Array.isArray(sectorWorkflow?.objects) && sectorWorkflow.objects.length > 0
      ? sectorWorkflow.objects
      : Array.isArray(sectorWorkflow?.additionalObjects)
        ? sectorWorkflow.additionalObjects
        : [];
  const specialRequests = Array.isArray(sectorWorkflow?.specialRequests) ? sectorWorkflow.specialRequests : [];

  const getName = (value: any): string => {
    if (!value) return "";
    if (typeof value === "string") return value.trim();

    return value?.name || value?.label || value?.title || value?.type || "";
  };

  const categoryName = getName(sectorWorkflow?.category) || getName(sectorWorkflow?.categories) || sectorWorkflow?.categoryName || "-";

  const typeOfPlaceName =
    getName(sectorWorkflow?.places) ||
    getName(sectorWorkflow?.place) ||
    getName(sectorWorkflow?.typeOfPlace) ||
    getName(sectorWorkflow?.typeOfPlaces) ||
    sectorWorkflow?.typeOfPlaceName ||
    "-";

  const originalServiceName = getName(sectorWorkflow?.service) || getName(sectorWorkflow?.typeOfService) || "Service";

  const serviceNameOverride = String(sectorWorkflow?.serviceNameOverride ?? "");
const [useCustomServiceName, setUseCustomServiceName] =
  useState(Boolean(serviceNameOverride.trim()));

useEffect(() => {
  setUseCustomServiceName(Boolean(serviceNameOverride.trim()));
}, [serviceNameOverride]);




 const displayedServiceName =
  useCustomServiceName && serviceNameOverride.trim()
    ? serviceNameOverride.trim()
    : originalServiceName;

  const updateServiceNameOverride = (value: string) => {
    dispatch(quoteCreateActions.updateServiceNameOverride(value));
  };

  const squareFeet = sectorWorkflow?.squareft || sectorWorkflow?.squareFootage || "-";
  const averagePeople = sectorWorkflow?.averagePeople ?? "-";

  const quoteAlias = String(summary?.quote?.alias || summary?.quote?.title || summary?.quote?.name || "").trim();

  const notes = String(sectorWorkflow?.notes || sectorWorkflow?.note || "").trim();

  const unitLabel = sectorWorkflow?.service?.measure || "Unit";

  const costOverride = sectorWorkflow?.costOverride ?? {
    by: EMonetaryAmountBy.UNIQUE,
    value: 0,
    currency: "USD",
  };

  const numericPrice = Number(costOverride?.value ?? 0);
  const [priceFocused, setPriceFocused] = useState(false);
  const [priceDraft, setPriceDraft] = useState(String(numericPrice));

  useEffect(() => {
    if (!priceFocused) {
      setPriceDraft(String(Number.isFinite(numericPrice) ? numericPrice : 0));
    }
  }, [numericPrice, priceFocused]);

  const commitPriceDraft = useCallback(() => {
    const raw = priceDraft.trim();
    if (raw === "" || raw === ".") {
      dispatch(quoteCreateActions.updateCostOverrideValue(0));
      setPriceDraft("0");
      return;
    }
    const n = Number(raw);
    dispatch(quoteCreateActions.updateCostOverrideValue(Number.isFinite(n) ? n : 0));
    setPriceDraft(String(Number.isFinite(n) ? n : 0));
  }, [dispatch, priceDraft]);

  if (loading) return <PaginationLoadingOverlay isVisible />;

  return (
    <Grid container spacing={3} sx={{ p: 2, alignItems: "flex-start" }}>
      <Grid item xs={12} lg={8}>
        {/* Quote Overview */}
        <Card sx={{ mb: 2, border: "none", borderRadius: 2 }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
                letterSpacing: 0.5,
              }}
            >
              Quote Overview
            </Typography>

            <Typography
              sx={{
                mt: 0.25,
                fontSize: 12,
                color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
              }}
            >
              Review the selected service and scope information.
            </Typography>
          </Box>

          {quoteAlias ? (
            <Box
              sx={{
                mb: 1.25,
                px: 1.25,
                py: 0.9,
                borderRadius: 2,
                bgcolor: alphaVar(colors.orange.base, 0.035),
                border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
              }}
            >
              <Typography
                sx={{
                  mb: 0.3,
                  fontSize: 10,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: colors.orange.base,
                }}
              >
                Quote alias
              </Typography>

              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
                  overflowWrap: "anywhere",
                }}
              >
                {quoteAlias}
              </Typography>
            </Box>
          ) : null}

          <FrequencyOverviewRow
            value={currentFrequencyType}
            isEditing={isEditingFrequency}
            onEdit={() => setIsEditingFrequency(true)}
            onCancel={() => setIsEditingFrequency(false)}
            onChange={(value) => {
              dispatch(quoteCreateActions.updateFrequencyType(value));
            }}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              gap: 1.25,
            }}
          >
            <QuoteSummaryCard title="Category" subtitle="Service category" value={categoryName} />

            <QuoteSummaryCard
              title="Type of Place"
              subtitle={squareFeet !== "-" ? `${squareFeet} sq ft` : "Selected property type"}
              value={typeOfPlaceName}
            />

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alphaVar(colors.orange.base, 0.035),
                border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
                minWidth: 0,
                gridColumn: {
                  xs: "1",
                  sm: "span 2",
                },
              }}
            >
              <Typography
                sx={{
                  mb: 0.5,
                  fontSize: 11,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: colors.orange.base,
                }}
              >
                Service
              </Typography>

              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
                  lineHeight: 1.35,
                  overflowWrap: "anywhere",
                }}
              >
                {displayedServiceName}
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: 11,
                  color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
                }}
              >
                {useCustomServiceName && serviceNameOverride ? `Original service: ${originalServiceName}` : "Selected service type"}
              </Typography>

            <ServiceAliasField
  enabled={useCustomServiceName}
  value={serviceNameOverride}
  originalServiceName={originalServiceName}
  size="small"
  description="This alias will be displayed on the quote, scope of work, and invoice."
  onEnabledChange={setUseCustomServiceName}
  onValueChange={updateServiceNameOverride}
/>
            </Box>

            <QuoteSummaryCard
              title="Areas & Tasks"
              subtitle={activeOtherAreas.length > 0 ? `${activeAreas.length} standard · ${activeOtherAreas.length} other` : "Selected work areas"}
              value={`${activeAreas.length + activeOtherAreas.length} areas`}
            />

            <QuoteSummaryCard title="Objects" subtitle="Additional service objects" value={`${selectedObjects.length} objects`} />

            <QuoteSummaryCard title="Average People" subtitle="Estimated staff" value={averagePeople} />

            <QuoteSummaryCard title="Special Requests" subtitle="Additional requests" value={`${specialRequests.length} requests`} />
          </Box>

          {notes ? (
            <Box
              sx={{
                mt: 1.25,
                px: 1.25,
                py: 1,
                borderRadius: 2,
                bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.025)",
                border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.05)",
              }}
            >
              <Typography
                sx={{
                  mb: 0.35,
                  fontSize: 11,
                  fontWeight: 800,
                  color: colors.gray?.[500] || "var(--fx-surface-textsecondary, #64748B)",
                }}
              >
                Notes
              </Typography>

              <Typography
                sx={{
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: colors.gray?.[700] || "var(--fx-surface-textsecondary, #64748B)",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                }}
              >
                {notes}
              </Typography>
            </Box>
          ) : null}
        </Card>

        {/* Areas & Tasks */}
        <Card sx={{ mb: 2, border: "none" }}>
          <Typography sx={{ fontWeight: 700, mb: 2, fontSize: 14 }}>Areas & Tasks</Typography>

          {activeAreas.length > 0 ? (
            <Stack gap={1}>
              {activeAreas.map((area: any, idx: number) => {
                const areaUid = String(area?.uid ?? idx);
                const areaQuantity = getAreaQuantity(area);
                const areaTasks = (Array.isArray(area?.tasks) ? area.tasks : []).filter((task: any) => task.active);
                const areaNotesCount = areaTasks.filter((task: any) => String(task?.note ?? "").trim().length > 0).length;
                const isAreaOpen = expandedAreaUid === areaUid;

                return (
                  <Box
                    key={areaUid}
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      onClick={() => setExpandedAreaUid(isAreaOpen ? null : areaUid)}
                      sx={{
                        p: 1.25,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        cursor: "pointer",
                        bgcolor: alphaVar(colors.orange.base, 0.035),
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: colors.gray[800],
                            lineHeight: 1.25,
                          }}
                        >
                          {area?.name || "Unnamed area"}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 11,
                            color: colors.gray[500],
                            mt: 0.25,
                          }}
                        >
                          {areaQuantity > 0 ? `${areaQuantity} ${unitLabel} · ` : ""}
                          {areaTasks.length} tasks
                          {areaNotesCount > 0 ? ` · ${areaNotesCount} notes` : ""}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          bgcolor: "var(--fx-surface-base, #FFFFFF)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: colors.orange.base,
                          flexShrink: 0,
                        }}
                      >
                        {isAreaOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                      </Box>
                    </Box>

                    <Collapse in={isAreaOpen} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 1.25, py: 1 }}>
                        {areaTasks.length > 0 ? (
                          <Stack gap={0.5}>
                            {areaTasks.map((task: any, tIdx: number) => {
                              const taskUid = String(task?.uid ?? `${areaUid}-${tIdx}`);
                              const taskName = task?.name ?? task?.task?.name ?? task?.description ?? "Unnamed task";
                              const note = String(task?.note ?? "").trim();
                              const hasNote = note.length > 0;
                              const isHovering = hoverTaskUid === taskUid;
                              const isNoteOpen = openNotePreviewUid === taskUid;

                              return (
                                <Box
                                  key={taskUid}
                                  sx={{
                                    px: 0.75,
                                    py: 0.35,
                                    borderRadius: 1.25,
                                    "&:hover": {
                                      bgcolor: alphaVar(colors.orange.base, 0.035),
                                    },
                                  }}
                                  onMouseEnter={() => setHoverTaskUid(taskUid)}
                                  onMouseLeave={() => setHoverTaskUid(null)}
                                >
                                  <Box
                                    sx={{
                                      minHeight: 34,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 13,
                                        color: colors.gray[700],
                                        flex: 1,
                                        minWidth: 0,
                                      }}
                                    >
                                      {taskName}
                                    </Typography>

                                    <Box
                                      sx={{
                                        width: 34,
                                        minWidth: 34,
                                        height: 30,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      {hasNote ? (
                                        <Tooltip title={isNoteOpen ? "Hide note" : "View note"} arrow placement="top">
                                          <ChatBubble
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenNotePreviewUid(isNoteOpen ? null : taskUid);
                                            }}
                                            sx={{
                                              fontSize: 16,
                                              color: isNoteOpen ? colors.orange[700] : colors.orange.base,
                                              cursor: "pointer",
                                            }}
                                          />
                                        </Tooltip>
                                      ) : (
                                        <Box
                                          sx={{
                                            opacity: isHovering ? 1 : 0,
                                            pointerEvents: isHovering ? "auto" : "none",
                                            transition: "opacity 120ms ease",
                                          }}
                                        >
                                          <TaskNoteBubbleForQuote
                                            areaUid={area?.uid}
                                            taskId={taskUid}
                                            taskName={taskName}
                                            noteFromStore={task?.note ?? ""}
                                            icon={<AddCircleOutline fontSize="small" />}
                                          />
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>

                                  <Collapse in={hasNote && isNoteOpen} timeout="auto" unmountOnExit>
                                    <Box
                                      sx={{
                                        mt: 0.5,
                                        mb: 0.5,
                                        p: 1,
                                        borderRadius: 1.5,
                                        bgcolor: alphaVar(colors.orange.base, 0.055),
                                        border: `1px solid ${alphaVar(colors.orange.base, 0.14)}`,
                                        display: "flex",
                                        alignItems: "flex-start",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: 12,
                                          color: colors.gray[600],
                                          lineHeight: 1.5,
                                          whiteSpace: "pre-wrap",
                                          overflowWrap: "anywhere",
                                          wordBreak: "break-word",
                                          flex: 1,
                                          minWidth: 0,
                                        }}
                                      >
                                        {note}
                                      </Typography>

                                      <Box
                                        sx={{
                                          width: 30,
                                          minWidth: 30,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          mt: -0.5,
                                          flexShrink: 0,
                                        }}
                                      >
                                        <TaskNoteBubbleForQuote
                                          areaUid={area?.uid}
                                          taskId={taskUid}
                                          taskName={taskName}
                                          noteFromStore={task?.note ?? ""}
                                          icon={<Edit fontSize="small" />}
                                        />
                                      </Box>
                                    </Box>
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </Stack>
                        ) : (
                          <Typography sx={{ fontSize: 13, color: colors.gray[500] }}>No tasks assigned.</Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 13, color: colors.gray[500] }}>No areas assigned.</Typography>
          )}
        </Card>

        {/* Other Areas */}
        {activeOtherAreas.length > 0 && (
          <Card sx={{ mb: 2, border: "none" }}>
            <Typography
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: 14,
              }}
            >
              Other Areas
            </Typography>

            <Stack gap={1}>
              {activeOtherAreas.map((area: any, idx: number) => {
                const areaUid = String(area?.uid ?? area?.id ?? `other-area-${idx}`);

                const areaQuantity = getAreaQuantity(area);

                const areaTasks = (Array.isArray(area?.tasks) ? area.tasks : []).filter((task: any) => task?.active);

                const areaNotesCount = areaTasks.filter((task: any) => String(task?.note ?? "").trim().length > 0).length;

                const isAreaOpen = expandedAreaUid === areaUid;

                return (
                  <Box
                    key={areaUid}
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      onClick={() => setExpandedAreaUid(isAreaOpen ? null : areaUid)}
                      sx={{
                        p: 1.25,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        cursor: "pointer",
                        bgcolor: alphaVar(colors.orange.base, 0.035),
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: colors.gray[800],
                            lineHeight: 1.25,
                          }}
                        >
                          {area?.name || "Unnamed other area"}
                        </Typography>

                        <Typography
                          sx={{
                            mt: 0.25,
                            fontSize: 11,
                            color: colors.gray[500],
                          }}
                        >
                          {areaQuantity > 0 ? `${areaQuantity} ${unitLabel} · ` : ""}
                          {areaTasks.length} {areaTasks.length === 1 ? "task" : "tasks"}
                          {areaNotesCount > 0 ? ` · ${areaNotesCount} ${areaNotesCount === 1 ? "note" : "notes"}` : ""}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          bgcolor: "var(--fx-surface-base, #FFFFFF)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: colors.orange.base,
                          flexShrink: 0,
                        }}
                      >
                        {isAreaOpen ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                      </Box>
                    </Box>

                    <Collapse in={isAreaOpen} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 1.25, py: 1 }}>
                        {areaTasks.length > 0 ? (
                          <Stack gap={0.5}>
                            {areaTasks.map((task: any, taskIndex: number) => {
                              const taskUid = String(task?.uid ?? task?.id ?? `${areaUid}-${taskIndex}`);

                              const taskName = task?.name ?? task?.task?.name ?? task?.description ?? "Unnamed task";

                              const note = String(task?.note ?? "").trim();

                              const hasNote = note.length > 0;

                              const taskStateUid = `${areaUid}:${taskUid}`;

                              const isHovering = hoverTaskUid === taskStateUid;

                              const isNoteOpen = openNotePreviewUid === taskStateUid;

                              return (
                                <Box
                                  key={taskUid}
                                  sx={{
                                    px: 0.75,
                                    py: 0.35,
                                    borderRadius: 1.25,
                                    "&:hover": {
                                      bgcolor: alphaVar(colors.orange.base, 0.035),
                                    },
                                  }}
                                  onMouseEnter={() => setHoverTaskUid(taskStateUid)}
                                  onMouseLeave={() => setHoverTaskUid(null)}
                                >
                                  <Box
                                    sx={{
                                      minHeight: 34,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: 13,
                                        color: colors.gray[700],
                                        flex: 1,
                                        minWidth: 0,
                                      }}
                                    >
                                      {taskName}
                                    </Typography>

                                    <Box
                                      sx={{
                                        width: 34,
                                        minWidth: 34,
                                        height: 30,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      {hasNote ? (
                                        <Tooltip title={isNoteOpen ? "Hide note" : "View note"} arrow placement="top">
                                          <ChatBubble
                                            onClick={(event) => {
                                              event.stopPropagation();

                                              setOpenNotePreviewUid(isNoteOpen ? null : taskStateUid);
                                            }}
                                            sx={{
                                              fontSize: 16,
                                              color: isNoteOpen ? colors.orange[700] : colors.orange.base,
                                              cursor: "pointer",
                                            }}
                                          />
                                        </Tooltip>
                                      ) : (
                                        <Box
                                          sx={{
                                            opacity: isHovering ? 1 : 0,
                                            pointerEvents: isHovering ? "auto" : "none",
                                            transition: "opacity 120ms ease",
                                          }}
                                        >
                                          <TaskNoteBubbleForQuote
                                            areaUid={areaUid}
                                            taskId={taskUid}
                                            taskName={taskName}
                                            noteFromStore={task?.note ?? ""}
                                            icon={<AddCircleOutline fontSize="small" />}
                                          />
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>

                                  <Collapse in={hasNote && isNoteOpen} timeout="auto" unmountOnExit>
                                    <Box
                                      sx={{
                                        mt: 0.5,
                                        mb: 0.5,
                                        p: 1,
                                        borderRadius: 1.5,
                                        bgcolor: alphaVar(colors.orange.base, 0.055),
                                        border: `1px solid ${alphaVar(colors.orange.base, 0.14)}`,
                                        display: "flex",
                                        alignItems: "flex-start",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        minWidth: 0,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: 12,
                                          color: colors.gray[600],
                                          lineHeight: 1.5,
                                          whiteSpace: "pre-wrap",
                                          overflowWrap: "anywhere",
                                          wordBreak: "break-word",
                                          flex: 1,
                                          minWidth: 0,
                                        }}
                                      >
                                        {note}
                                      </Typography>

                                      <Box
                                        sx={{
                                          width: 30,
                                          minWidth: 30,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          mt: -0.5,
                                          flexShrink: 0,
                                        }}
                                      >
                                        <TaskNoteBubbleForQuote
                                          areaUid={areaUid}
                                          taskId={taskUid}
                                          taskName={taskName}
                                          noteFromStore={task?.note ?? ""}
                                          icon={<Edit fontSize="small" />}
                                        />
                                      </Box>
                                    </Box>
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </Stack>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: 13,
                              color: colors.gray[500],
                            }}
                          >
                            No tasks assigned.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Stack>
          </Card>
        )}

        {/* Objects */}
        {selectedObjects.length > 0 && (
          <Card sx={{ mb: 2, border: "none" }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5, fontSize: 14 }}>Objects</Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(4, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(5, 1fr)",
                },
                gap: 1.25,
              }}
            >
              {selectedObjects.map((object: any) => (
                <Box
                  key={object?.uid}
                  sx={{
                    height: 120,
                    borderRadius: 2,
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                    border: `1px solid ${alphaVar(colors.orange.base, 0.12)}`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 1,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 58,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1,
                    }}
                  >
                    {object?.imageUrl ? (
                      <Box
                        component="img"
                        src={object.imageUrl}
                        alt={object?.name || "Object"}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 20,
                          fontWeight: 800,
                          color: colors.orange.base,
                        }}
                      >
                        {String(object?.name || "?").charAt(0)}
                      </Typography>
                    )}
                  </Box>

                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: colors.gray[700],
                      textAlign: "center",
                      lineHeight: 1.25,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {object?.name || "Unnamed object"}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        )}

        {/* Special Requests */}
        {specialRequests.length > 0 && (
          <Card sx={{ mb: 2, border: "none" }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5, fontSize: 14 }}>Special Requests</Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(4, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(5, 1fr)",
                },
                gap: 1.25,
              }}
            >
              {specialRequests.map((request: any) => (
                <Box
                  key={request?.uid}
                  sx={{
                    height: 120,
                    borderRadius: 2,
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                    border: `1px solid ${alphaVar(colors.orange.base, 0.12)}`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 1,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 58,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1,
                    }}
                  >
                    {request?.imageUrl ? (
                      <Box
                        component="img"
                        src={request.imageUrl}
                        alt={request?.name || "Special request"}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 20,
                          fontWeight: 800,
                          color: colors.orange.base,
                        }}
                      >
                        {String(request?.name || "?").charAt(0)}
                      </Typography>
                    )}
                  </Box>

                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: colors.gray[700],
                      textAlign: "center",
                      lineHeight: 1.25,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {request?.name || "Special request"}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        )}
      </Grid>

      <Grid
        item
        xs={12}
        lg={4}
        sx={{
          position: { lg: "sticky" },
          top: { lg: 7 },
          alignSelf: "flex-start",
        }}
      >
        <Stack spacing={2}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: 3,
              boxShadow: `0 12px 32px ${alphaVar(colors.orange.base, 0.08)}, 0 2px 8px rgba(0,0,0,0.04)`,
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
            }}
          >
            <Box
              sx={{
                height: 3,
                borderRadius: "3px 3px 0 0",
              }}
            />

            <Box sx={{ p: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: alphaVar(colors.orange.base, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
                      stroke={colors.orange.base}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
                      lineHeight: 1.2,
                    }}
                  >
                    Pricing
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 12,
                      color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
                      mt: 0.25,
                    }}
                  >
                    Define the service price for this quote.
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 12,
                        textTransform: "uppercase",
                        letterSpacing: 0.6,
                        color: colors.gray?.[500] || "var(--fx-surface-textsecondary, #64748B)",
                        mb: 0.75,
                      }}
                    >
                      Pricing Type
                    </Typography>

                    <Select
                      MenuProps={menuPropsModal}
                      size="small"
                      displayEmpty
                      value={costOverride?.by || EMonetaryAmountBy.UNIQUE}
                      onChange={(event) => dispatch(quoteCreateActions.updateCostOverrideBy(event.target.value as EMonetaryAmountBy))}
                      renderValue={(selected) => {
                        if (!selected) return "Select type";

                        return monetaryAmountOptions.find((option) => option.value === selected)?.label || selected;
                      }}
                      sx={{
                        borderRadius: 2,
                        bgcolor: colors.gray?.[50] || "var(--fx-surface-sunken, #F8FAFC)",
                        fontSize: 13,
                        color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
                        transition: "all 0.15s",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.gray?.[200] || "var(--fx-surface-border, #E2E8F0)",
                        },
                        "& .MuiSelect-icon": {
                          color: colors.gray?.[500] || "var(--fx-surface-textsecondary, #64748B)",
                        },
                        "&:hover": {
                          bgcolor: alphaVar(colors.orange.base, 0.04),
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: alphaVar(colors.orange.base, 0.4),
                        },
                        "&.Mui-focused": {
                          bgcolor: "var(--fx-surface-base, #FFFFFF)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.orange.base,
                          borderWidth: "1px",
                        },
                        "&.Mui-focused .MuiSelect-icon": {
                          color: colors.orange.base,
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select type
                      </MenuItem>

                      {monetaryAmountOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          sx={{
                            color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
                            "&.Mui-selected": {
                              backgroundColor: alphaVar(colors.orange.base, 0.12),
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: alphaVar(colors.orange.base, 0.18),
                            },
                          }}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <FormLabel
                      sx={{
                        fontWeight: 600,
                        fontSize: 12,
                        textTransform: "uppercase",
                        letterSpacing: 0.6,
                        color: colors.gray?.[500] || "var(--fx-surface-textsecondary, #64748B)",
                        mb: 0.75,
                      }}
                    >
                      Amount (USD)
                    </FormLabel>

                    <TextField
                      type="text"
                      inputMode="decimal"
                      size="small"
                      value={priceFocused ? priceDraft : String(Number.isFinite(numericPrice) ? numericPrice : 0)}
                      onFocus={() => {
                        setPriceFocused(true);
                        setPriceDraft(String(Number.isFinite(numericPrice) ? numericPrice : 0));
                      }}
                      onBlur={() => {
                        setPriceFocused(false);
                        commitPriceDraft();
                      }}
                      onChange={(event) => {
                        setPriceDraft(normalizePriceDraft(event.target.value));
                      }}
                      placeholder="0.00"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: colors.gray?.[50] || "var(--fx-surface-sunken, #F8FAFC)",
                          fontSize: 13,
                          transition: "all 0.15s",
                          "&:hover": {
                            bgcolor: alphaVar(colors.orange.base, 0.04),
                            "& fieldset": {
                              borderColor: alphaVar(colors.orange.base, 0.4),
                            },
                          },
                          "&.Mui-focused": {
                            bgcolor: "var(--fx-surface-base, #FFFFFF)",
                            "& fieldset": {
                              borderColor: colors.orange.base,
                            },
                          },
                        },
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              {(costOverride?.by || costOverride?.value != null) && (
                <Box
                  sx={{
                    mt: 2.5,
                    p: 2,
                    borderRadius: 2,
                    background: `linear-gradient(
                135deg,
                ${alphaVar(colors.orange.base, 0.07)} 0%,
                ${alphaVar(colors.orange.base, 0.03)} 100%
              )`,
                    border: `1px solid ${alphaVar(colors.orange.base, 0.15)}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        color: colors.orange.base,
                        mb: 0.25,
                      }}
                    >
                      Override Active
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 13,
                        color: colors.gray?.[600] || "var(--fx-surface-textsecondary, #64748B)",
                      }}
                    >
                      {monetaryAmountOptions.find((option) => option.value === (costOverride?.by || EMonetaryAmountBy.UNIQUE))?.label || "—"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      textAlign: "right",
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: colors.orange.base,
                      boxShadow: `0 4px 12px ${alphaVar(colors.orange.base, 0.35)}`,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 800,
                        color: "#fff",
                        lineHeight: 1,
                      }}
                    >
                      {money(Number(costOverride?.value ?? 0))}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );
};

type CardProps = {
  children: ReactNode;
  sx?: any;
};

const Card = ({ children, sx }: CardProps) => (
  <Box
    sx={{
      p: 2,
      border: `1px solid ${colors.gray?.[100] || "var(--fx-surface-border, #E2E8F0)"}`,
      borderRadius: 2,
      bgcolor: "var(--fx-surface-base, #FFFFFF)",
      ...sx,
    }}
  >
    {children}
  </Box>
);

interface QuoteSummaryCardProps {
  title: string;
  subtitle?: string;
  value?: ReactNode;
}

export const QuoteSummaryCard = ({ title, subtitle, value }: QuoteSummaryCardProps) => {
  const isEmpty = value === undefined || value === null || value === "" || value === "-";

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        bgcolor: alphaVar(colors.orange.base, 0.035),
        border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
        minWidth: 0,
        transition: "all 140ms ease",
        "&:hover": {
          bgcolor: alphaVar(colors.orange.base, 0.055),
          borderColor: alphaVar(colors.orange.base, 0.18),
        },
      }}
    >
      <Typography
        sx={{
          mb: 0.35,
          fontSize: 11,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: colors.orange.base,
        }}
      >
        {title}
      </Typography>

      <Typography
        component="div"
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: isEmpty ? colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)" : colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
          lineHeight: 1.35,
          overflowWrap: "anywhere",
        }}
      >
        {isEmpty ? "Not selected" : value}
      </Typography>

      {subtitle ? (
        <Typography
          sx={{
            mt: 0.35,
            fontSize: 11,
            lineHeight: 1.35,
            color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
          }}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
};

const FrequencyOverviewRow = ({
  value,
  isEditing,
  onEdit,
  onCancel,
  onChange,
}: {
  value: string;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onChange: (val: string) => void;
}) => {
  const displayLabel = getJobFrequencyLabel(value);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 0.5,
        px: 1.25,
        mb: 1.25,
        borderRadius: 2,
        bgcolor: alphaVar(colors.orange.base, 0.035),
        border: `1px solid ${alphaVar(colors.orange.base, 0.1)}`,
        gap: 2,
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: colors.orange.base,
        }}
      >
        Frequency
      </Typography>

      {isEditing ? (
        <Select
          autoFocus
          size="small"
          value={value || EFrequencyType.WEEKLY}
          onChange={(event: any) => {
            onChange(event.target.value);
            onCancel();
          }}
          onClose={onCancel}
          MenuProps={menuPropsModal}
          sx={{
            minWidth: 160,
            fontSize: 13,
            fontWeight: 600,
            height: 32,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          }}
        >
          {JOB_FREQUENCY_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13, fontWeight: 500 }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: colors.gray?.[800] || "var(--fx-surface-text, #0F172A)",
              textAlign: "right",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayLabel}
          </Typography>

          <Tooltip title="Edit frequency">
            <Box
              onClick={onEdit}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
                borderRadius: 1,
                cursor: "pointer",
                color: colors.gray?.[400] || "var(--fx-surface-texttertiary, #94A3B8)",
                "&:hover": {
                  bgcolor: alphaVar(colors.orange?.base || "#f97316", 0.1),
                  color: colors.orange?.base || "#f97316",
                },
              }}
            >
              <Edit sx={{ fontSize: 15 }} />
            </Box>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};
